import React, { useEffect, useRef } from 'react'
import { cn } from '@amusnet/react-ui'
import { useTranslation } from '@amusnet/translations/react'
import { useUltraPlayStore, useConfigurationStore, usePlayerInfoStore } from '@stores'
import { formatCurrency } from '@amusnet/amusnet-common'
import UltraPlaySpinRow, { SPECIAL_ROW_CLASS, getUltraPlayRowGrid } from './UltraPlaySpinRow'

interface UltraPlayTableProps {
    isSetup?: boolean
}

const UltraPlayTable: React.FC<UltraPlayTableProps> = ({ isSetup = false }) => {
    const { t } = useTranslation()
    const spins = useUltraPlayStore((s) => s.spins)
    const startingBalance = useUltraPlayStore((s) => s.startingBalance)
    const status = useUltraPlayStore((s) => s.status)
    const currentSpinNumber = useUltraPlayStore((s) => s.currentSpinNumber)
    const currency = useConfigurationStore((s) => s.currency)
    const hideCents = useConfigurationStore((s) => s.currencyHideCents)
    const gridClass = getUltraPlayRowGrid(useConfigurationStore((s) => s.viewport))
    const liveBalance = usePlayerInfoStore((s) => s.balance)

    const tableBodyRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (tableBodyRef.current) {
            tableBodyRef.current.scrollTop = 0
        }
    }, [spins.length])

    const isFinished = status === 'READY' && spins.length > 0

    const balanceToShow = startingBalance > 0 ? startingBalance : liveBalance

    const startingBalanceRow = (
        <div
            className={cn(
                'flex items-center justify-between px-[40px] py-4 rounded-xl bg-black flex-shrink-0',
                SPECIAL_ROW_CLASS
            )}
        >
            <span className="text-[24px] font-bold leading-[24px] text-white uppercase">
                {t('common.startingBalance')}
            </span>
            <span id="ultra-play-starting-balance" className="text-[24px] font-bold leading-[24px] text-white">
                {formatCurrency(balanceToShow, { hideCents })}
                <span className="text-[18px] ml-1">{currency}</span>
            </span>
        </div>
    )

    return (
        <div className={cn('flex flex-col min-h-0 bg-[#1F1F1F] rounded-xl p-[10px] gap-[10px]', !isSetup && 'h-full')}>
            {/* Spin header — same fixed grid as the rows so the labels sit above their columns */}
            <div
                className={cn(
                    'grid items-center flex-shrink-0 px-[40px] pb-[8px] text-[24px] font-bold leading-[24px] text-ultra-header',
                    gridClass
                )}
            >
                <span id="ultra-play-current-spin" className="col-start-1">
                    {t('common.spin')} {currentSpinNumber}
                </span>
                <span className="col-start-2 text-right">{t('gameui.winUppercase')}</span>
                <span className="col-start-4 text-left">X</span>
                <span className="col-start-5 text-right">{t('common.balance')}</span>
            </div>

            {/* Setup mode: Starting Balance pinned at top */}
            {isSetup && startingBalanceRow}

            {/* Scrollable rows — only in full mode. touch-pan-y (on the container and every descendant)
                re-enables vertical touch scrolling, which the global `div { touch-action: none }` in
                index.css — used to lock the game canvas — otherwise blocks on mobile. */}
            {!isSetup && (
                <div
                    ref={tableBodyRef}
                    className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-[10px] touch-pan-y [&_*]:touch-pan-y"
                >
                    {/* END BALANCE — shown at top when history */}
                    {isFinished && (
                        <div
                            className={cn(
                                'flex items-center justify-between px-[40px] py-4 rounded-xl bg-black flex-shrink-0',
                                SPECIAL_ROW_CLASS
                            )}
                        >
                            <span className="text-[24px] font-bold leading-[24px] text-white uppercase">
                                {t('common.endBalance')}
                            </span>
                            <span
                                id="ultra-play-end-balance"
                                className="text-[24px] font-bold leading-[24px] text-white"
                            >
                                {spins.length > 0 ? formatCurrency(spins[0].balance, { hideCents }) : '—'}
                                <span className="text-[18px] ml-1">{currency}</span>
                            </span>
                        </div>
                    )}

                    {spins.map((spin, index) => (
                        <UltraPlaySpinRow key={spin.spinNumber} spin={spin} isSticky={index === 0 && !isFinished} />
                    ))}

                    {spins.length === 0 && status !== 'READY' && (
                        <div className="flex items-center justify-center py-6 text-white/30 text-[12px]">—</div>
                    )}

                    {/* Starting Balance pinned at bottom in full mode */}
                    {startingBalanceRow}
                </div>
            )}
        </div>
    )
}

export default UltraPlayTable
